import { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { WizardContent, AvatarPreview } from '.';
import { PreGameWizard } from '../../types/pre-game';
import { PlayerContext } from '../../contexts/player';
import { joinPlayer, uploadAvatar } from '../../services/players';
import { GameDocumentContext } from '../../contexts/game-document';
import { UpdatePlayerStateAsync } from '../../utils/game-engine/base';
import { PlayerResponse } from '../../types/responses/player-response';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import { generateTitleById } from '../../utils/game-document/display-languages';

const StepFive = ({ nextStepCallback, currentStep = 1 }: PreGameWizard) => {
  const navigate = useNavigate();
  const { gameCode } = useParams();
  const [gameDocument] = useContext(GameDocumentContext);
  const [playerState, setPlayerState] = useContext(PlayerContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFinishButton = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      let avatar = playerState.playerState?.avatarImage;
      if (avatar && typeof avatar === 'string' && avatar.startsWith('blob:')) {
        const blob = await fetch(avatar).then((r) => r.blob());
        const extension = blob.type.split('/')[1];
        const file = new File(
          [blob],
          `${playerState.playerState?.name}-${gameCode}.${extension}`,
          { type: blob.type }
        );
        const response = await uploadAvatar(file);
        if (response) avatar = response.result;
      }

      const response: PlayerResponse = await joinPlayer(
        gameDocument.gameCode ?? '',
        playerState.playerState?.code ?? '',
        playerState.playerState?.name ?? '',
        avatar
      );
      if (response) {
        setPlayerState((prev) =>
          UpdatePlayerStateAsync(prev, {
            ...prev.playerState!,
            name: response.name,
            avatarImage: response.avatar,
            status: 'ready'
          })
        );

        const navigateToLoby = () => {
          navigate(`lobby`);
        };

        if (!gameDocument?.theme) {
          navigateToLoby();
        } else {
          if (
            gameDocument?.theme?.logoUrl === '' ||
            gameDocument?.theme?.logoUrl === undefined
          ) {
            navigateToLoby();
          } else {
            navigate(`splash`);
          }
        }
      }
    } catch (error) {
      console.error('Failed to join game:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadAvatar = () => {
    nextStepCallback(currentStep - 1);
  };

  return (
    <WizardContent
      additionalContainerClass={'position-relative'}
      isShowTitle={false}
      nextButtonText={
        isSubmitting
          ? 'Submitting...'
          : generateTitleById(
              '5f243f13-e05b-448b-8a19-b45e16f9719c',
              gameDocument,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'Finish'
      }
      disableButton={isSubmitting}
      additionalButtonContainerClass={'w-100'}
      additionalButtonNextClass={'mt-4'}
      handleNextButtonClicked={handleFinishButton}>
      <AvatarPreview
        isShowPencil={!isSubmitting}
        avatarSource={playerState.playerState?.avatarImage}
        handleUploadAvatar={handleUploadAvatar}
      />
    </WizardContent>
  );
};

export default StepFive;
